import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {ItemPicker, type ItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {Octicon} from '@primer/styled-react/deprecated'
import type React from 'react'
import {type Dispatch, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import {useIssueFiltering} from '../hooks/useIssueFiltering'
import {getIssueIconAndFill} from '../utils/issue-icon'
import type {IssuePickerIssue$data} from './__generated__/IssuePickerIssue.graphql'
import type {
  IssuePickerSelectedIssuesQuery,
  IssuePickerSelectedIssuesQuery$data,
} from './__generated__/IssuePickerSelectedIssuesQuery.graphql'
import styles from './IssuePicker.module.css'

const getItemKey = (issue: IssuePickerItem) => issue.id

export type IssuePickerItem = Omit<IssuePickerIssue$data, ' $fragmentType'> & {
  parent?: {
    readonly id: string
  } | null
}

export type IssueState = 'CLOSED' | 'OPEN'
export type IssueStateReason = 'COMPLETED' | 'DUPLICATE' | 'NOT_PLANNED' | 'REOPENED'

type PropsFromItemPicker = Partial<
  Pick<
    ItemPickerProps<IssuePickerItem>,
    'className' | 'selectionVariant' | 'subtitle' | 'notice' | 'insidePortal' | 'responsiveOnNarrow'
  >
>

type IssuePickerBaseProps = {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  onIssueUpdate?: () => void
  onIssueSelection: (issues: IssuePickerItem[]) => void
  // onItemSelect is called when the user explicitly clicks an item (passed as onAction to each item).
  // Useful for distinguishing an intentional selection/deselection from the picker
  // being dismissed without interaction (e.g. clicking outside).
  onItemSelect?: () => void
  repositoryNameWithOwner?: string
  title?: string | React.ReactElement
  triggerOpen?: boolean
  onClose?: () => void
  selectedIssueIds?: string[]
  hiddenIssueIds?: string[]
  pickerId?: string
  withParent?: boolean
} & PropsFromItemPicker

export type IssuePickerProps = IssuePickerBaseProps & {
  owner?: string
  isLoading?: boolean
}

type IssuePickerInternalProps = IssuePickerBaseProps & {
  items: Map<string, IssuePickerItem>
  isLoading: boolean
  filter: string
  setFilter: Dispatch<string>
}

const selectedGroup = {groupId: 'selected', header: {title: 'Selected'}}
const suggestionGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}
const groups: ItemGroup[] = [selectedGroup, suggestionGroup]

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssuePickerSelectedIssuesGraphQLQuery = graphql`
  query IssuePickerSelectedIssuesQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueFragment = graphql`
  fragment IssuePickerIssue on Issue {
    id
    title
    state
    stateReason
    repository {
      id
      nameWithOwner
    }
    # Adding this to the main fragment, although it's not used to avoid introducing relay in Memex
    # eslint-disable-next-line relay/unused-fields
    databaseId
    number
    # Used for optimistic updates when closing an issue as duplicate
    # eslint-disable-next-line relay/unused-fields
    url
    __typename
  }
`

const emptyMap = new Map<string, IssuePickerItem>()

export function IssuePicker({
  onIssueSelection,
  selectedIssueIds,
  hiddenIssueIds,
  owner,
  repositoryNameWithOwner,
  pickerId,
  isLoading: isLoadingExternally = false,
  withParent = false,
  ...rest
}: IssuePickerProps) {
  const [filter, setFilter] = useState('')
  const {isLoading, isError, items} = useIssueFiltering(
    filter,
    owner,
    repositoryNameWithOwner,
    hiddenIssueIds,
    withParent,
  )

  // Render a fallback if there is an error fetching the data from the filter
  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('issues'),
    anchorElement: rest.anchorElement,
    open: true,
  })
  if (isError) {
    return createFallbackComponent(() => {})
  }

  return (
    <IssuePickerInternal
      // When there is an external loading state, act as if the internal query is still loading as well
      // and pass any empty items array. This helps to prevent layout shift in the UI when an external
      // query loads after we've initially rendered the UI.
      items={isLoadingExternally ? emptyMap : items}
      isLoading={isLoading || isLoadingExternally}
      {...rest}
      selectedIssueIds={selectedIssueIds}
      hiddenIssueIds={hiddenIssueIds}
      filter={filter}
      setFilter={setFilter}
      onIssueSelection={onIssueSelection}
      repositoryNameWithOwner={repositoryNameWithOwner}
      pickerId={pickerId}
    />
  )
}

function IssuePickerInternal({
  items,
  onIssueSelection,
  anchorElement,
  setFilter,
  isLoading,
  title,
  onClose,
  triggerOpen,
  selectedIssueIds,
  filter,
  pickerId,
  selectionVariant = 'single',
  subtitle,
  className,
  notice,
  insidePortal,
  responsiveOnNarrow,
  onItemSelect,
}: IssuePickerInternalProps) {
  const environment = useRelayEnvironment()
  const [fetchedSelectedIssues, setFetchedSelectedIssues] = useState<Record<string, IssuePickerItem>>({})

  const fetchSelectedIssues = useCallback(
    (ids: string[]) => {
      return new Promise<IssuePickerSelectedIssuesQuery$data>((resolve, reject) => {
        clientSideRelayFetchQueryRetained<IssuePickerSelectedIssuesQuery>({
          environment,
          query: IssuePickerSelectedIssuesGraphQLQuery,
          variables: {ids},
        }).subscribe({
          next: (fetchedData: IssuePickerSelectedIssuesQuery$data) => {
            if (fetchedData !== null) {
              resolve(fetchedData)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment],
  )

  const syncSelectedIssues = useEffectEvent(() => {
    async function doFetch(missingIssueIds: string[]) {
      const {nodes = []} = await fetchSelectedIssues(missingIssueIds)
      for (const node of nodes) {
        if (!node || node.__typename !== 'Issue') continue
        setFetchedSelectedIssues(loadedSelectedIssuesMap => {
          return {...loadedSelectedIssuesMap, [node.id]: node}
        })
      }
    }

    // Check if the selected issues are already fetched and cache
    // them to avoid bugs when they're not returned in subsequent queries
    if (selectedIssueIds) {
      const missingIssueIds = []
      for (const selectedIssueId of selectedIssueIds) {
        const foundSelectedIssue = items.get(selectedIssueId)

        if (foundSelectedIssue) {
          setFetchedSelectedIssues(loadedSelectedIssuesMap => {
            return {...loadedSelectedIssuesMap, [selectedIssueId]: foundSelectedIssue}
          })
        } else if (!fetchedSelectedIssues?.[selectedIssueId]) {
          missingIssueIds.push(selectedIssueId)
        }
      }

      if (missingIssueIds.length > 0) {
        doFetch(missingIssueIds)
      }
    }
  })

  useEffect(() => {
    // Wait until the data is fetched before fetching any missing selected issues
    if (isLoading) return

    // We only need to run this after the initial query, as we'll have
    // cached the selected issues for subsequent queries.
    syncSelectedIssues()
  }, [isLoading, items])

  // Merge the fetched selected issues with the queried issues and ensure there are no duplicates
  const mergedItems = new Map(items)
  for (const [id, issue] of Object.entries(fetchedSelectedIssues)) {
    mergedItems.set(id, issue)
  }

  const allIssues = Array.from(mergedItems.values())

  // If the search term is a number and there is an issue with that number, move it to the front.
  // If the issue is already in the 0th position, do nothing.
  const foundIssueNumberIndex = allIssues.findIndex(i => i.number === Number(filter))
  if (foundIssueNumberIndex > 0) {
    const [issue] = allIssues.splice(foundIssueNumberIndex, 1)

    if (issue) {
      allIssues.unshift(issue)
    }
  }

  const convertToItemProps = useCallback(
    (issue: IssuePickerItem) => {
      const {icon, fill} = getIssueIconAndFill(issue.state, issue.stateReason)
      const issueIsSelected = selectedIssueIds?.includes(issue.id)

      return {
        id: issue.id,
        text: issue.title,
        sx: {wordBreak: 'break-word'},
        source: issue,
        groupId: issueIsSelected ? selectedGroup.groupId : suggestionGroup.groupId,
        leadingVisual: () => <Octicon icon={icon} sx={{path: {fill}}} />,
        trailingVisual: () => <span className={styles.issueNumberText}>{`#${issue.number}`}</span>,
        // Show NWO when the issue is selected as it might reside in another repository than what is being queried.
        description: issueIsSelected ? issue.repository.nameWithOwner : undefined,
        descriptionVariant: 'block' as const,
        onAction: onItemSelect,
      }
    },
    [selectedIssueIds, onItemSelect],
  )

  const issuePickerRef = useRef<HTMLButtonElement>(null)

  const itemPickerGroups = useMemo(
    () => (selectedIssueIds?.length ? groups : groups.filter(group => group.groupId !== selectedGroup.groupId)),
    [selectedIssueIds],
  )

  const showNoMatchMessage = useMemo(() => allIssues.length === 0, [allIssues.length])
  return (
    <ItemPicker
      loading={isLoading}
      items={allIssues}
      initialSelectedItems={selectedIssueIds || []}
      filterItems={setFilter}
      title={title || LABELS.issueHeader}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText="Search issues"
      selectionVariant={selectionVariant}
      onSelectionChange={onIssueSelection}
      renderAnchor={anchorElement}
      height="large"
      width="medium"
      maxVisibleItems={-1}
      resultListAriaLabel="issue results"
      selectPanelRef={issuePickerRef}
      triggerOpen={triggerOpen}
      groups={itemPickerGroups}
      onClose={onClose}
      pickerId={pickerId}
      subtitle={subtitle}
      className={className}
      notice={notice}
      suppressNoMatchItem
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No issues found',
              body: 'Try changing your search term or selecting a different repository.',
              variant: 'empty',
            }
          : undefined
      }
      insidePortal={insidePortal}
      responsiveOnNarrow={responsiveOnNarrow}
    />
  )
}
